/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges} from '@angular/core';

@Component({
  selector: 'gm-loading-block',
  standalone: true,
  imports: [CommonModule],
  template: '',
})
export class GmLoadingBlockComponent implements OnChanges, OnDestroy {
  @Input() target: unknown;
  @Input() blocked = false;
  @Input() label = 'Loading';

  private overlayElement: HTMLElement | null = null;

  constructor(private renderer: Renderer2) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blocked'] || changes['target'] || changes['label']) {
      this.syncOverlay();
    }
  }

  ngOnDestroy(): void {
    this.removeOverlay();
  }

  private syncOverlay(): void {
    if (!this.blocked) {
      this.removeOverlay();
      return;
    }

    const targetElement = this.getTargetElement();
    if (!targetElement) {
      this.removeOverlay();
      return;
    }

    if (!this.overlayElement) {
      this.overlayElement = this.renderer.createElement('div');
      this.renderer.addClass(this.overlayElement, 'gm-loading-block-overlay');
    }

    this.overlayElement.innerHTML = `<span class="pi pi-spin pi-spinner"></span><span>${this.label}</span>`;

    const computedPosition = getComputedStyle(targetElement).position;
    if (computedPosition === 'static' || !computedPosition) {
      this.renderer.setStyle(targetElement, 'position', 'relative');
    }

    if (!targetElement.contains(this.overlayElement)) {
      this.renderer.appendChild(targetElement, this.overlayElement);
    }
  }

  private removeOverlay(): void {
    if (!this.overlayElement?.parentElement) {
      return;
    }
    this.renderer.removeChild(this.overlayElement.parentElement, this.overlayElement);
  }

  private getTargetElement(): HTMLElement | null {
    const maybeTarget = this.target as any;
    const candidate = maybeTarget?.nativeElement ?? maybeTarget?.el?.nativeElement ?? maybeTarget;
    return candidate instanceof HTMLElement ? candidate : null;
  }
}
